import {
  Box,
  Divider,
  Modal,
  Skeleton,
  Stack,
  styled,
  Typography
} from '@mui/material';
import Avatar from '@mui/material/Avatar';
import * as React from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { authActions } from '../../redux/auth/slice';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme } from '../../theme/theme.config';

interface IProfileModalProps {
  open: boolean;
  handleClose: () => void;
  handleProfilePictureModalOpen: () => void;
  handleTelNoModalOpen: () => void;
}

const StyledModal = styled(Modal)(() => {
  const matches = useMediaQuery('(min-width:400px)');

  return {
    position: 'fixed',
    top: '20px',
    left: 'auto',
    right: matches ? '20px' : '0px',
    height: matches ? '540px' : '565px',
    width: matches ? '380px' : '100%',
    borderRadius: '15px',
    transform: 'translate(0, 0)',
    boxShadow: '0px 2px 6px 2px #00000026',
    margin: matches ? '20px' : '20px 0px',
    '& .MuiBackdrop-root': {
      backgroundColor: '#FFFFFF',
      borderRadius: '15px'
    }
  };
});

const FieldNameText = styled(Typography)(() => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: '400',
  fontSize: '14px',
  lineHeight: '24px',
  letterSpacing: '0.5px',
  color: '#1A1B1F'
}));

const FieldDataText = styled(Typography)(() => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: '400',
  fontSize: '14px',
  lineHeight: '24px',
  letterSpacing: '0.5px',
  color: '#1A1B1F'
}));

export default function ProfileModal({
  open,
  handleClose
}: IProfileModalProps): JSX.Element {
  const isNotMobile = useMediaQuery('(min-width:400px)');

  const handleSignOut = (): void => {
    handleClose();
    dispatch(authActions.getSignOutUrl());
  };

  const dispatch = useAppDispatch();
  const { profileData } = useAppSelector((state) => state.profileData);

  const name = useAppSelector(
    (state) => state.profileData.profileData?.displayName
  );
  const loading = useAppSelector(
    (state) => state.profileData.getProfileLoading
  );

  const updateLoading = useAppSelector(
    (state) => state.profileData.updatePhoneNumberLoading
  );

  const getProfileDataError = useAppSelector(
    (state) => state.profileData.getProfileError
  );
  const imageUrl = useAppSelector(
    (state) => state.profileData.profileData?.profilePic
  );

  const componentRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        componentRef.current &&
        !componentRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [componentRef]);

  return (
    <StyledModal data-testid="profile-modal" open={open} onClose={handleClose}>
      <Box ref={componentRef} sx={{ outline: 'none' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#D7E2FF',
            borderRadius: '3px',
            margin: '10px 10px 20px 10px'
          }}
        >
          {isNotMobile ? (
            <>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '12px',
                  fontWeight: '500',
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  textAlign: 'center',
                  color: 'rgba(48, 48, 52, 0.51)'
                }}
              >
                This account is managed by
              </Typography>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '12px',
                  fontWeight: '800',
                  lineHeight: '24px',
                  letterSpacing: '0.15px',
                  textAlign: 'center',
                  marginLeft: '3px',
                  color: '#1A1B1F'
                }}
              >
                fcodelabs.com
              </Typography>
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: 'grid',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Typography>
                  <span
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '12px',
                      fontWeight: '500',
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      textAlign: 'center',
                      color: 'rgba(48, 48, 52, 0.51)'
                    }}
                  >
                    This account is managed by
                  </span>
                  <span
                    style={{
                      fontFamily: theme.typography.fontFamily,
                      fontSize: '12px',
                      fontWeight: '800',
                      lineHeight: '24px',
                      letterSpacing: '0.15px',
                      textAlign: 'center',
                      marginLeft: '3px',
                      color: '#1A1B1F'
                    }}
                  >
                    fcodelabs.com
                  </span>
                </Typography>
              </Box>
            </>
          )}
        </Box>

        {getProfileDataError ? (
          <Box
            sx={{
              fontFamily: 'Be Vietnam Pro',
              fontStyle: 'normal',
              fontWeight: 400,
              fontSize: '15px',
              lineHeight: '28px',
              height: '25vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              color: 'red'
            }}
          >
            {getProfileDataError}
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-evenly',
                alignItems: 'center',
                margin: '50px 0 30px 0'
              }}
            >
              {!loading ? (
                <Box
                  sx={{
                    position: 'relative',
                    width: '150px',
                    height: '150px'
                  }}
                >
                  <Avatar
                    sx={{
                      backgroundColor: '#00BF7A',
                      width: '150px',
                      height: '150px',
                      position: 'relative'
                    }}
                  >
                    {imageUrl ? (
                      <Avatar
                        src={imageUrl}
                        sx={{
                          width: '150px',
                          height: '150px',
                          scale: '1'
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          fontFamily: theme.typography.fontFamily,
                          fontWeight: '500',
                          fontSize: '96px',
                          lineHeight: '112px',
                          textAlign: 'center',
                          color: '#FFFFFF'
                        }}
                      >
                        {name?.charAt(0)}
                      </Typography>
                    )}
                  </Avatar>
                </Box>
              ) : (
                <Skeleton variant="circular" width={'150px'} height={'150px'} />
              )}
            </Box>

            {!loading ? (
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: '600',
                  fontSize: '16px',
                  lineHeight: '19px',
                  textAlign: 'center',
                  color: '#000000',
                  margin: '10px'
                }}
              >
                {profileData?.displayName}
              </Typography>
            ) : (
              <Stack direction={'column'} alignItems={'center'}>
                <Skeleton
                  variant="text"
                  sx={{ fontSize: '16px', width: '60%' }}
                />
                <Skeleton
                  variant="text"
                  sx={{ fontSize: '16px', width: '75%' }}
                />
              </Stack>
            )}

            <Typography
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: '400',
                fontSize: '14px',
                lineHeight: '16.41px',
                textAlign: 'center',
                color: '#44474E',
                margin: '10px'
              }}
            >
              {profileData?.userPrincipalName}
            </Typography>

            <Box sx={{ marginTop: '30px', padding: '10px' }}>
              <Box sx={{ margin: '10px' }}>
                <FieldNameText>Designation</FieldNameText>
                {!loading ? (
                  <FieldDataText>{profileData?.jobTitle}</FieldDataText>
                ) : (
                  <Skeleton
                    variant="text"
                    sx={{ fontSize: '16px', width: '95%' }}
                  />
                )}
              </Box>
              <Box sx={{ margin: '10px' }}>
                <Box sx={{ display: 'flex' }}>
                  <FieldNameText>Telephone Number</FieldNameText>
                </Box>
                {!loading && !updateLoading ? (
                  <FieldDataText>
                    {profileData?.mobilePhone
                      ? profileData?.mobilePhone
                      : 'N/A'}
                  </FieldDataText>
                ) : (
                  <Skeleton
                    variant="text"
                    sx={{ fontSize: '16px', width: '95%' }}
                  />
                )}
              </Box>
            </Box>
          </>
        )}

        <Divider sx={{ color: 'CAC4D0' }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <Box
            sx={{
              width: '100px',
              border: '1px solid #AEC6FF',
              padding: '5px 5px 5px 5px',
              borderRadius: '10px',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '10px'
            }}
          >
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: '500',
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.1px',
                textAlign: 'center',
                color: '#44474E'
              }}
              data-testid="sign-out"
              onClick={handleSignOut}
            >
              Sign out
            </Typography>
          </Box>
        </Box>
      </Box>
    </StyledModal>
  );
}
